import { Dispatch } from 'redux';

import { logger } from '@nykaa/logger';
import { pushData } from '@nykaa/data-layer/utils';
import { actionForSSRDataLayer } from '@nykaa/data-layer/store/action';
import statusCodes from 'constants/statusCodes';
import appEventTypes from 'shared/store/app/actionTypes';
import { getHomePageWidgets as getBannerWidgets } from 'shared/store/home/service';
import footerActionTypes from 'shared/store/footer/actionTypes';
import { DYNAMIC_FOOTER_LINK_API_ERR_MESSAGE } from 'shared/constants';
import actionTypes from './actionTypes';
import { getDataLayerForProduct, extractProductJourneyData } from './helpers';
import { getProduct, getProductOffers, getDynamicFooterLinks } from './service';
import {
  FetchBannerParams,
  FetchProductParams,
  OffersParams,
  Product,
} from './types';
import { defaultDataLayer } from 'constants/dataLayer';
import { DWEB } from 'constants/devices';

interface ResolutionData {
  productData: Product | null;
  errorCode: number | null;
}

export const fetchProduct = (params: FetchProductParams) => (dispatch: any) => {
  // FIXME: type for dispatch
  dispatch({
    type: actionTypes.FETCH_PRODUCT_DATA_PROGRESS,
  });

  return getProduct(params)
    .then((productData) => {
      dispatch({
        type: actionTypes.FETCH_PRODUCT_DATA_SUCCESS,
        payload: productData,
      });
      return {
        productData,
        errorCode: null,
      };
    })
    .catch((err) => {
      let status = statusCodes.ERROR;
      if (err.isAxiosError && err.response?.status) {
        status = err.response.status;
      }

      logger.error(
        err,
        `Error in Fetch Product action ${JSON.stringify(params)}`,
        { code: status }
      );

      dispatch({
        type: appEventTypes.APP_REDUCER_STATUS_CODE,
        payload: status,
      });
      dispatch({
        type: actionTypes.FETCH_PRODUCT_DATA_FAILED,
      });

      return {
        productData: null,
        errorCode: status,
      };
    })
    .then(({ productData, errorCode }: ResolutionData) => {
      const dataLayerForProduct = getDataLayerForProduct(
        productData,
        errorCode,
        params
      );
      if (__SERVER__) {
        dispatch(actionForSSRDataLayer(dataLayerForProduct));
      } else {
        const defaultData = defaultDataLayer();
        pushData({
          ...defaultData,
          ...dataLayerForProduct,
          spaPageView: Date.now(),
          event: 'spaPageView',
        });
      }
      return productData;
    });
};

export const changeCurrentVariant = (childId: string) => (dispatch: any) => {
  dispatch({
    type: actionTypes.CHANGE_PRODUCT_VARIANT,
    payload: childId,
  });
};

export const fetchProductOffers =
  (params: OffersParams, isOfferApiV2Enabled?: boolean) => (dispatch: any) => {
    dispatch({
      type: actionTypes.FETCH_OFFERS_PROGRESS,
    });
    return getProductOffers(params, isOfferApiV2Enabled)
      .then((offers) => {
        if (offers && offers.length > 0) {
          dispatch({
            type: actionTypes.FETCH_OFFERS_SUCCESS,
            payload: offers,
          });
        }
      })
      .catch((err) => {
        dispatch({
          type: actionTypes.FETCH_OFFERS_FAILED,
        });
        return Promise.reject(err);
      });
  };

export const trackProductJourney =
  (queryString: string) => (dispatch: Dispatch) => {
    // TODO: extract tracking data from url string
    const trackingData = extractProductJourneyData(queryString);
    if (trackingData) {
      if (__SERVER__) {
        dispatch(actionForSSRDataLayer(trackingData));
      } else {
        pushData(trackingData, true);
      }
    }
  };

export const changeProductQuesAndRatingTab =
  (activeTab: number) => (dispatch: Dispatch) => {
    dispatch({
      type: actionTypes.PRODUCT_REVIEW_ACTIVE_TAB,
      payload: activeTab,
    });
  };

export const fetchBanners =
  (params: FetchBannerParams, isPaginationEnabled?: boolean) =>
  (dispatch: Dispatch) => {
    const PAGE_TYPE_FOR_PD_BANNERS = 'pd-page';

    const requestParams = {
      page_type: PAGE_TYPE_FOR_PD_BANNERS,
      page_data: params.productId,
      api_version: isPaginationEnabled ? 'V2' : '',
      dn_platform: DWEB,
    };

    return getBannerWidgets(requestParams, '', isPaginationEnabled)
      .then(({ widgets, remaining_inventories_ids, queryParams }) => {
        if (Array.isArray(widgets)) {
          dispatch({
            type: actionTypes.BANNERS_SUCCESS,
            payload: {
              widgets,
              remaining_inventories_ids,
              queryParamsObject: { ...queryParams },
            },
          });
        }
      })
      .catch(() => {
        dispatch({
          type: 'pd-banner-fail',
        });
      });
  };

export const productUnMount = () => ({
  type: actionTypes.PRODUCT_UNMOUNT,
});

export const fetchDynamicFooterLinks = (params: any) => (dispatch: Dispatch) =>
  getDynamicFooterLinks(params)
    .then((response) => {
      dispatch({
        type: footerActionTypes.FETCH_DYNAMIC_FOOTER_LINKS,
        payload: response,
      });
      return response;
    })
    .catch((err) => {
      logger.error(err, DYNAMIC_FOOTER_LINK_API_ERR_MESSAGE);
    });
